import React, { useCallback } from 'react';
import { FormattedMessage, FormattedRelativeTime, useIntl } from '@atlassian/jira-intl';

import { messages } from './messages.tsx';

export const MILLISECOND = 1;
export const SECOND = MILLISECOND * 1000;
export const MINUTE = SECOND * 60;
export const HOUR = MINUTE * 60;
export const DAY = HOUR * 24;
export const WEEK = DAY * 7;
export const MONTH = DAY * 30;
export const YEAR = DAY * 365;

type Unit = 'second' | 'minute' | 'hour' | 'day' | 'week';

const WithUpdatedLabel = (formattedDate?: string) => (
	<FormattedMessage
		{...(formattedDate ? messages.overOneMinuteText : messages.underOneMinuteText)}
		values={{ date: formattedDate }}
	/>
);

export function formatDateOnly(dateNum: number | string, locale = 'en-US') {
	const date = new Date(Number(dateNum));
	const day = String(date.getDate()).padStart(2, '0');
	const month = date.toLocaleString(locale, { month: 'short' });
	const year = date.getFullYear();
	return `${day} ${month}, ${year}`;
}

export const DateFormatter = ({ timestamp }: { timestamp: number | string }) => {
	const locale = useIntl().locale;

	const calculateTimeDiff = useCallback(
		(): number => Math.floor(Number(timestamp) - Date.now()),
		[timestamp],
	);

	const getTimeUnit = (): {
		value: number;
		unit: Unit;
	} | null => {
		const millisecondsSinceUpdate = calculateTimeDiff();
		const absMilliseconds = Math.abs(millisecondsSinceUpdate);

		if (absMilliseconds < MINUTE) {
			return {
				value: 0,
				unit: 'second',
			};
		}

		if (absMilliseconds < HOUR) {
			return {
				value: Math.floor(millisecondsSinceUpdate / MINUTE),
				unit: 'minute',
			};
		}

		if (absMilliseconds < DAY) {
			return {
				value: Math.floor(millisecondsSinceUpdate / HOUR),
				unit: 'hour',
			};
		}

		if (absMilliseconds < WEEK) {
			return {
				value: Math.floor(millisecondsSinceUpdate / DAY),
				unit: 'day',
			};
		}

		if (absMilliseconds < 2 * WEEK) {
			return {
				value: Math.floor(millisecondsSinceUpdate / WEEK),
				unit: 'week',
			};
		}

		return null;
	};

	const timeUnit = getTimeUnit();

	if (timeUnit?.value === 0 && timeUnit.unit === 'second') {
		return <FormattedMessage {...messages.underOneMinuteText} />;
	}

	if (!timeUnit) {
		return <span>{formatDateOnly(timestamp, locale)}</span>;
	}

	return (
		<FormattedRelativeTime value={timeUnit.value} unit={timeUnit.unit}>
			{WithUpdatedLabel}
		</FormattedRelativeTime>
	);
};
